import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { TEST_SYNC_FERTIG_EVENT, getTestSyncAenderungen, type SyncAenderungen } from '../lib/api'
import { useUmgebung } from '../lib/umgebung'

/** Alle paar Minuten nachsehen — Änderungen kommen von Kollegen, nicht nur aus diesem Tab. */
const ABSTAND_MS = 120_000

/**
 * Im Production-Admin über jeder Seite, solange Änderungen noch nicht auf
 * Test sind: nicht wegklickbar, denn genau das vergisst man sonst. Weg ist er
 * erst nach „Nach Test synchronisieren“ (Konfiguration → Testumgebung).
 */
export function SyncHinweis({ onZurKonfiguration }: { onZurKonfiguration: () => void }) {
  const umgebung = useUmgebung()
  const [stand, setStand] = useState<SyncAenderungen | null>(null)

  useEffect(() => {
    if (umgebung !== 'production') return
    let aktiv = true
    const laden = () =>
      getTestSyncAenderungen()
        .then((s) => aktiv && setStand(s))
        .catch(() => {})
    void laden()
    const t = setInterval(() => void laden(), ABSTAND_MS)
    const onFertig = () => void laden()
    window.addEventListener(TEST_SYNC_FERTIG_EVENT, onFertig)
    // Nach jeder eigenen Änderung im Admin stimmt die Zahl sonst erst beim nächsten Nachsehen.
    const onSichtbar = () => document.visibilityState === 'visible' && void laden()
    document.addEventListener('visibilitychange', onSichtbar)
    return () => {
      aktiv = false
      clearInterval(t)
      window.removeEventListener(TEST_SYNC_FERTIG_EVENT, onFertig)
      document.removeEventListener('visibilitychange', onSichtbar)
    }
  }, [umgebung])

  if (umgebung !== 'production' || !stand || (stand.anzahl === 0 && !stand.letzterFehler)) {
    return null
  }
  const anzahl = stand.anzahl === 1 ? 'Eine Änderung ist' : `${stand.anzahl} Änderungen sind`
  const fehler = stand.letzterFehler
  return (
    <div
      role={fehler ? 'alert' : 'status'}
      data-testid="sync-hinweis"
      className={`mb-5 flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3 text-sm text-text ${
        fehler ? 'border-status-error bg-status-error/10' : 'border-brand/40 bg-brand/10'
      }`}
    >
      <RefreshCw
        size={18}
        className={`shrink-0 ${fehler ? 'text-status-error' : 'text-brand'}`}
        aria-hidden="true"
      />
      <p className="min-w-0 flex-1">
        {fehler ? (
          <>
            <span className="font-semibold">Automatischer Abgleich fehlgeschlagen</span> (
            {zeit(fehler.at)}): {fehler.text}
            {stand.anzahl > 0 && ` — ${anzahl} noch nicht auf Test.`}
          </>
        ) : (
          <>
            <span className="font-semibold">Nicht synchronisierte Änderungen.</span> {anzahl} noch
            nicht auf Test
            {stand.letzterAbgleich
              ? ` (letzter Abgleich ${zeit(stand.letzterAbgleich)})`
              : ' (noch nie synchronisiert)'}
            . Spätestens heute Nacht automatisch, oder jetzt per Knopf.
          </>
        )}
      </p>
      <button type="button" className="btn-chip shrink-0" onClick={onZurKonfiguration}>
        Zur Testumgebung
      </button>
    </div>
  )
}

function zeit(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
